import React from "react";
import { StatusBadge } from "./Shared";
import { formatDate, formatDateTime, formatMoney } from "./utils";

const Row = ({ label, children }) => (
    <>
        <dt className="text-slate-400 text-xs sm:text-sm pt-0.5">{label}</dt>
        <dd className="text-slate-700 text-xs sm:text-sm break-words">{children}</dd>
    </>
);

const DetailsModal = ({ open, subject, isAdmin, onClose }) => {
    if (!open || !subject) return null;
    const { kind, data } = subject;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div
                className="w-full max-w-lg max-h-[85vh] rounded-xl bg-white border border-slate-200 flex flex-col overflow-hidden"
                style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}
            >
                {/* Sticky header so the title stays visible while the body scrolls */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                    <h2 className="text-lg font-bold text-slate-800 truncate pr-4">
                        {kind === "server" ? data.server_name : data.company_name}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 shrink-0">✕</button>
                </div>

                <div className="px-6 py-4 overflow-y-auto">
                    {kind === "server" ? (
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-3">
                            <Row label="Server Port">{data.server_port}</Row>
                            <Row label="Server Name">{data.server_name}</Row>
                            <Row label="Provider Name">{data.provider_name || "—"}</Row>
                            <Row label="Capacity">{`RAM- ${data.ram || "—"} / ROM- ${data.rom || "—"}`}</Row>
                            <Row label="Server User ID">{data.server_user_id || "—"}</Row>
                            <Row label="Server Password">{data.server_password || "—"}</Row>
                            <Row label="Total Users">{data.number_of_users ?? 0}</Row>
                            <Row label="Active / Inactive">{data.active_users ?? 0} / {data.inactive_users ?? 0}</Row>

                            {isAdmin && (
                                <>
                                    <Row label="Cost Price">{formatMoney(data.cost_price)}</Row>
                                    <Row label="Sell Price">
                                        {data.sellByCurrency && Object.keys(data.sellByCurrency).length > 0
                                            ? Object.entries(data.sellByCurrency).map(([cur, amt]) => <div key={cur}>{formatMoney(amt, cur)}</div>)
                                            : "—"}
                                    </Row>
                                    <Row label="Profit %">{data.profitPercent != null ? `${data.profitPercent}%` : "—"}</Row>
                                </>
                            )}

                            <Row label="Created">{formatDateTime(data.created_at)}</Row>
                        </dl>
                    ) : (
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-3">
                            <Row label="User ID">{data.user_id}</Row>
                            <Row label="Company Name">{data.company_name}</Row>
                            <Row label="Contact Person">{data.contact_person_name || "—"}</Row>
                            <Row label="Contact Number">{data.contact_person_number || "—"}</Row>
                            <Row label="Username">{data.user_name || "—"}</Row>
                            <Row label="Password">{data.user_password || "—"}</Row>
                            <Row label="Licence Type"><span className="capitalize">{data.licence_type || "—"}</span></Row>
                            <Row label="Server Port">{data.server_port}</Row>
                            <Row label="Actual Username">{data.actual_user_name || "—"}</Row>
                            <Row label="Actual Password">{data.actual_user_password || "—"}</Row>
                            <Row label="Status"><StatusBadge status={data.status} /></Row>
                            <Row label="Activation Date">{formatDate(data.activation_date)}</Row>
                            <Row label="Expire Date">{formatDate(data.expire_date)}</Row>
                            <Row label="Next Renewal">{formatDate(data.next_renewal_date)}</Row>
                            <Row label="Prorata Expire">{formatDate(data.prorata_expire_date)}</Row>
                            <Row label="Rate">{formatMoney(data.rate, data.currency)}</Row>
                            <Row label="Discount">{formatMoney(data.discount || 0, data.currency)}</Row>
                            <Row label="Total Amount">{formatMoney(data.total_amount, data.currency)}</Row>
                            <Row label="Subscription">{data.subscription ? `${data.subscription} month(s)` : "—"}</Row>
                            <Row label="Gateway Path">{data.gateway_path || "—"}</Row>
                            <Row label="Referred Person">{data.referred_person || "—"}</Row>
                            <Row label="Referred Number">{data.referred_person_number || "—"}</Row>
                            <Row label="Sell Person">{data.sell_person_name || "—"}</Row>
                            <Row label="Region">{data.region || "—"}</Row>
                            <Row label="Renewal Status"><StatusBadge status={data.renewal_status} /></Row>
                            <Row label="Last Renewal By">{data.last_renewal_by || "—"}</Row>
                            <Row label="Last Renewal At">{formatDateTime(data.last_renewal_at)}</Row>
                            <Row label="Reason">{data.reason || "—"}</Row>
                            <Row label="Remarks">{data.remarks || "—"}</Row>
                            <Row label="Created By">{data.created_by || "—"}</Row>
                            <Row label="Created">{formatDateTime(data.created_at || data.user_created_at)}</Row>
                            {/* is_active, is_update and updated_at are intentionally omitted here */}
                        </dl>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DetailsModal;
